// Mensaje que ve el admin del HCE tras dar de baja a un médico (PLAN Sala, C11.2). El servidor (PATCH /api/hce/usuarios/:id) responde `baja`
// (qué pasó con sus turnos de hoy y qué especialidades quedan afectadas) o `reasignacionFallida` (la baja SÍ quedó hecha, pero sus
// turnos no se pudieron mover). Lógica pura, sin React, para probarla aparte.
//
// C11.3: ANTES de la baja se muestran las citas por atender y se exige elegir un reemplazo o dejarlas sin médico (`ResumenPreviaBaja`,
// `errorDeBaja`, `cargaDeBaja`); tras la baja, `traspaso` dice a quién pasaron.

export type ResumenBajaProfesional = {
  /** Turnos abiertos de hoy que cambiaron de dueño: `a` es el Practitioner de respaldo (o del reemplazo), o null si quedaron sin médico. */
  ticketsMovidos: Array<{ ticketId: number; a: string | null }>;
  /** Turnos en consulta del médico: NO se tocan, los resuelve un admin desde la Sala. */
  enConsultaSinTocar: number;
  especialidadesAfectadas: Array<{ id: string; name: string; esPrincipal: boolean; esRespaldo: boolean; citasFuturas: number }>;
  citasFuturas: number;
};

/** Lo que el servidor decidió con las citas futuras (C11.3): a quién pasaron (`null` = quedaron sin médico por decisión explícita). */
export type TraspasoDeBaja = { reemplazoNombre: string | null; citasFuturas: number; especialidades: string[]; turnosMovidos: number };

export type AvisoBaja = { nivel: 'alerta' | 'info'; texto: string };

export const TEXTO_REASIGNACION_FALLIDA =
  'Se dio de baja pero no se pudieron reasignar los pacientes en espera: un administrador debe reasignarlos en la Sala.';

const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;

const textoDeTraspaso = (t: TraspasoDeBaja): string =>
  `${t.citasFuturas > 0 ? (t.citasFuturas === 1 ? '1 cita por atender pasó' : `${t.citasFuturas} citas por atender pasaron`) : 'Sus especialidades pasaron'} a ${t.reemplazoNombre}.`;

/** `null` = no hay nada que avisar (la baja no movió turnos ni deja especialidades afectadas). */
export function avisoDeBaja(respuesta: { baja?: ResumenBajaProfesional | null; reasignacionFallida?: boolean; traspaso?: TraspasoDeBaja | null }): AvisoBaja | null {
  if (respuesta.reasignacionFallida) return { nivel: 'alerta', texto: TEXTO_REASIGNACION_FALLIDA };
  const baja = respuesta.baja;
  const traspaso = respuesta.traspaso?.reemplazoNombre ? respuesta.traspaso : null;
  if (!baja) return traspaso ? { nivel: 'info', texto: textoDeTraspaso(traspaso) } : null;

  const conMedico = baja.ticketsMovidos.filter((t) => t.a !== null).length;
  const sinMedico = baja.ticketsMovidos.length - conMedico;
  // Los que pasaron al reemplazo elegido no son del «médico de respaldo»: se cuentan aparte.
  const alReemplazo = Math.min(traspaso?.turnosMovidos ?? 0, conMedico);
  const alRespaldo = conMedico - alReemplazo;
  const partes: string[] = [];
  if (traspaso) partes.push(textoDeTraspaso(traspaso));
  if (alReemplazo > 0 && traspaso) {
    partes.push(`${plural(alReemplazo, 'paciente en espera pasó', 'pacientes en espera pasaron')} a ${traspaso.reemplazoNombre}.`);
  }
  if (alRespaldo > 0) {
    partes.push(`${plural(alRespaldo, 'paciente en espera pasó', 'pacientes en espera pasaron')} al médico de respaldo.`);
  }
  if (sinMedico > 0) {
    partes.push(`${plural(sinMedico, 'paciente en espera quedó', 'pacientes en espera quedaron')} sin médico asignado: asígnelos en la Sala.`);
  }
  if (baja.enConsultaSinTocar > 0) {
    partes.push(
      `${plural(baja.enConsultaSinTocar, 'paciente en consulta no se tocó', 'pacientes en consulta no se tocaron')}: un administrador debe resolverlos en la Sala.`,
    );
  }
  if (baja.especialidadesAfectadas.length > 0) {
    const detalle = baja.especialidadesAfectadas
      .map((e) => (e.citasFuturas > 0 ? `${e.name} (${plural(e.citasFuturas, 'cita futura', 'citas futuras')})` : e.name))
      .join(', ');
    partes.push(`Especialidades afectadas: ${detalle}.${baja.citasFuturas > 0 ? ' Vincule otro médico en el panel del consultorio.' : ''}`);
  }
  if (partes.length === 0) return null;
  const requiereAccion = sinMedico > 0 || baja.enConsultaSinTocar > 0 || baja.citasFuturas > 0;
  return { nivel: requiereAccion ? 'alerta' : 'info', texto: partes.join(' ') };
}

// ---------------------------------------------------------------------------
// Antes de la baja (PLAN Sala C11.3): citas por atender y a quién pasarlas
// ---------------------------------------------------------------------------

/** Lo que devuelve `GET /api/hce/usuarios/:id/baja-resumen`: solo conteos, fechas y nombres de profesionales y especialidades (nunca pacientes). */
export type ResumenPreviaBaja = {
  citasFuturas: number;
  desde: string | null;
  hasta: string | null;
  especialidades: Array<{ id: string; name: string; citasFuturas: number; esPrincipal: boolean; esRespaldo: boolean; cubreCitas: boolean; otrosActivos: number }>;
  ticketsPorMover: number;
  enConsulta: number;
  requiereDecision: boolean;
  reemplazos: Array<{ practitionerId: string; nombre: string }>;
};

/** Valor de la opción «Dejarlas sin médico»; `''` = todavía no eligió; cualquier otro valor es el `Practitioner.id` del reemplazo. */
export const ELIGE_SIN_MEDICO = '__sin_medico__';

export const TEXTOS_BAJA = {
  elegir: 'Elija quién atenderá las citas, o confirme que quedarán sin médico.',
  reemplazoNoDisponible: 'Ese médico ya no está disponible. Cierre este cuadro, actualice la lista y vuelva a intentarlo.',
  sinOtroMedico: 'No hay otro médico disponible para recibir estas citas.',
  pregunta: '¿Quién atenderá las citas?',
  sinMedico: 'Dejarlas sin médico (quedan marcadas «Sin médico activo»)',
  confirmar: 'Dar de baja',
  cancelar: 'Cancelar',
  cargando: 'Revisando sus citas…',
} as const;

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** `2026-11-02T15:00:00.000Z` -> «2 de noviembre» en hora de Ecuador (UTC-5 fijo, sin horario de verano). */
function diaEnLetras(iso: string): string {
  const g = new Date(new Date(iso).getTime() - 5 * 3600 * 1000);
  return `${g.getUTCDate()} de ${MESES[g.getUTCMonth()]}`;
}

export function rangoDeCitas(desde: string | null, hasta: string | null): string {
  if (!desde || !hasta) return '';
  const a = diaEnLetras(desde);
  const b = diaEnLetras(hasta);
  return a === b ? `el ${a}` : `del ${a} al ${b}`;
}

const citas = (n: number): string => (n === 1 ? '1 cita' : `${n} citas`);

/** ¿Hay algo que mostrar antes de dar de baja? Si no (sin citas, sin turnos, sin especialidades), basta la confirmación de siempre. */
export function bajaNecesitaPanel(r: ResumenPreviaBaja): boolean {
  return r.requiereDecision || r.ticketsPorMover > 0 || r.enConsulta > 0 || r.especialidades.length > 0;
}

/** Líneas del cuadro previo a la baja, en lenguaje sencillo y solo con datos del resumen. */
export function lineasDeBaja(r: ResumenPreviaBaja): string[] {
  const l: string[] = [];
  if (r.citasFuturas > 0) {
    const rango = rangoDeCitas(r.desde, r.hasta);
    l.push(`Tiene ${citas(r.citasFuturas)} por atender${rango ? ` (${rango})` : ''}.`);
    const conCitas = r.especialidades.filter((e) => e.citasFuturas > 0).map((e) => `${e.name} (${e.citasFuturas})`);
    if (conCitas.length > 0) l.push(`Especialidades: ${conCitas.join(', ')}.`);
  } else {
    l.push('No tiene citas por atender.');
  }
  if (r.ticketsPorMover > 0) l.push(`Hoy tiene ${r.ticketsPorMover === 1 ? '1 paciente' : `${r.ticketsPorMover} pacientes`} en espera.`);
  if (r.enConsulta > 0) l.push(`${r.enConsulta === 1 ? '1 paciente en consulta no se mueve' : `${r.enConsulta} pacientes en consulta no se mueven`}: ciérrelos desde la Sala.`);
  return l;
}

/** Texto de por qué no se puede continuar con esa elección (o `null`). */
export function errorDeBaja(r: ResumenPreviaBaja, eleccion: string): string | null {
  if (eleccion === '') return r.requiereDecision ? TEXTOS_BAJA.elegir : null;
  if (eleccion === ELIGE_SIN_MEDICO) return null;
  return r.reemplazos.some((x) => x.practitionerId === eleccion) ? null : TEXTOS_BAJA.reemplazoNoDisponible;
}

/** Lo que se suma al PATCH `{ active: false }`: el servidor exige exactamente una de las dos cuando hay citas por atender. */
export function cargaDeBaja(eleccion: string): { reemplazoId?: string; dejarSinMedico?: true } {
  if (eleccion === '') return {};
  return eleccion === ELIGE_SIN_MEDICO ? { dejarSinMedico: true } : { reemplazoId: eleccion };
}
